/**
 * Sales Trend (Retail Overview). One series — Net Sales or Orders — so no
 * legend; the card title names it. Daily line/area for multi-day periods,
 * hourly bars for a single day, both in tenant-local time (the server
 * buckets; this only draws).
 */
import React, { useEffect, useRef, useState } from "react";
import { count, dayLabel, hourLabel, money, moneyShort } from "../../lib/retailReporting";

const SERIES = "#264073";
const SERIES_FILL = "rgba(38, 64, 115, 0.10)";
const GRID = "#EEEAE3";
const H = 230;
const M = { l: 48, r: 12, t: 12, b: 26 };

function niceStep(raw) {
  const p = Math.pow(10, Math.floor(Math.log10(raw || 1)));
  const f = raw / p;
  return (f <= 1 ? 1 : f <= 2 ? 2 : f <= 2.5 ? 2.5 : f <= 5 ? 5 : 10) * p;
}

function useWidth(ref, fallback = 640) {
  const [w, setW] = useState(fallback);
  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const measure = () => setW(Math.max(280, el.clientWidth || fallback));
    measure();
    if (typeof ResizeObserver === "undefined") return undefined;
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [ref, fallback]);
  return w;
}

export default function SalesTrendChart({ trend, metric = "net", throughHour = null }) {
  const host = useRef(null);
  const W = useWidth(host);
  const [hover, setHover] = useState(null);
  const hourly = trend?.granularity === "hour";
  let points = (trend?.points || []).map((p) => ({ ...p, v: metric === "net" ? p.net_sales_cents : p.orders }));
  if (hourly) {
    const active = points.filter((p) => p.orders > 0).map((p) => Number(p.bucket));
    const first = Math.min(9, ...(active.length ? active : [9]));
    const last = Math.max(21, ...(active.length ? active : [21]));
    points = points.filter((p) => Number(p.bucket) >= first && Number(p.bucket) <= last);
  }
  const max = Math.max(1, ...points.map((p) => p.v));
  const step = niceStep(max / 4);
  const top = Math.ceil(max / step) * step;
  const iw = W - M.l - M.r;
  const ih = H - M.t - M.b;
  const y = (v) => M.t + ih - (v / top) * ih;
  const fmtY = (v) => (metric === "net" ? moneyShort(v) : count(v));
  const ticks = [];
  for (let v = 0; v <= top + 1e-9; v += step) ticks.push(v);
  const label = (p) => (hourly ? `${hourLabel(p.bucket)} – ${hourLabel((Number(p.bucket) + 1) % 24)}` : dayLabel(p.bucket, { weekday: true }));

  let marks = null;
  let xOf = () => 0;
  if (hourly) {
    const bw = iw / Math.max(1, points.length);
    const w = Math.min(28, bw - 6);
    xOf = (i) => M.l + bw * i + bw / 2;
    marks = points.map((p, i) => {
      const future = throughHour !== null && Number(p.bucket) > throughHour;
      const h = M.t + ih - y(p.v);
      return (
        <g key={p.bucket}>
          {!future && p.v > 0 && (
            <path d={`M${xOf(i) - w / 2},${M.t + ih}V${y(p.v) + 4}Q${xOf(i) - w / 2},${y(p.v)} ${xOf(i) - w / 2 + 4},${y(p.v)}H${xOf(i) + w / 2 - 4}Q${xOf(i) + w / 2},${y(p.v)} ${xOf(i) + w / 2},${y(p.v) + 4}V${M.t + ih}Z`}
              fill={SERIES} style={{ opacity: h > 0 ? 1 : 0 }} />
          )}
          {(points.length <= 14 || i % 2 === 0) && (
            <text x={xOf(i)} y={H - 6} textAnchor="middle">{hourLabel(p.bucket)}</text>
          )}
          <rect x={M.l + bw * i} y={M.t} width={bw} height={ih} fill="transparent"
            onMouseEnter={() => setHover({ i, future })} onMouseLeave={() => setHover(null)} />
        </g>
      );
    });
  } else {
    const n = points.length;
    xOf = (i) => M.l + (n === 1 ? iw / 2 : (i / (n - 1)) * iw);
    const line = points.map((p, i) => `${i ? "L" : "M"}${xOf(i).toFixed(1)},${y(p.v).toFixed(1)}`).join("");
    const every = Math.ceil(n / Math.max(2, Math.floor(iw / 70)));
    marks = (
      <>
        {n > 0 && <path d={`${line}L${xOf(n - 1).toFixed(1)},${M.t + ih}L${xOf(0).toFixed(1)},${M.t + ih}Z`} fill={SERIES_FILL} />}
        {n > 0 && <path d={line} fill="none" stroke={SERIES} strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" />}
        {points.map((p, i) => ((n - 1 - i) % every === 0 ? (
          <text key={p.bucket} x={xOf(i)} y={H - 6} textAnchor={i === 0 ? "start" : i === n - 1 ? "end" : "middle"}>{dayLabel(p.bucket)}</text>
        ) : null))}
        {n > 0 && <circle cx={xOf(n - 1)} cy={y(points[n - 1].v)} r="4" fill={SERIES} stroke="#fff" strokeWidth="2" />}
        {hover && (
          <>
            <line x1={xOf(hover.i)} x2={xOf(hover.i)} y1={M.t} y2={M.t + ih} stroke="#8A909C" strokeDasharray="3 3" />
            <circle cx={xOf(hover.i)} cy={y(points[hover.i].v)} r="5" fill={SERIES} stroke="#fff" strokeWidth="2" />
          </>
        )}
        <rect x={M.l} y={M.t} width={iw} height={ih} fill="transparent"
          onMouseMove={(e) => {
            const r = e.currentTarget.ownerSVGElement.getBoundingClientRect();
            const px = (e.clientX - r.left) * (W / r.width);
            const i = Math.max(0, Math.min(n - 1, Math.round((px - M.l) / (iw / Math.max(1, n - 1)))));
            setHover({ i });
          }}
          onMouseLeave={() => setHover(null)} />
      </>
    );
  }

  const hp = hover ? points[hover.i] : null;
  return (
    <div ref={host} className="relative w-full" data-testid="sales-trend-chart">
      <svg viewBox={`0 0 ${W} ${H}`} width="100%" height={H} role="img"
        aria-label={`Sales trend, ${hourly ? "by hour" : "by day"}`} className="block overflow-visible">
        <g style={{ font: "11.5px 'DM Sans', system-ui, sans-serif", fill: "#8A909C", fontVariantNumeric: "tabular-nums" }}>
          {ticks.map((v) => (
            <g key={v}>
              <line x1={M.l} x2={W - M.r} y1={y(v)} y2={y(v)} stroke={GRID} />
              <text x={M.l - 8} y={y(v) + 4} textAnchor="end">{fmtY(v)}</text>
            </g>
          ))}
          {marks}
        </g>
      </svg>
      {hp && (
        <div className="pointer-events-none absolute z-10 rounded-lg bg-foreground px-3 py-2 text-xs text-background shadow-lg"
          style={{ left: Math.min(Math.max(0, xOf(hover.i) - 70), W - 160), top: 0 }} role="status">
          <div className="font-semibold">{label(hp)}</div>
          {hover.future ? <div>Later today</div> : (
            <>
              <div className="flex justify-between gap-4 tabular-nums"><span>Net Sales</span><span>{money(hp.net_sales_cents)}</span></div>
              <div className="flex justify-between gap-4 tabular-nums"><span>Orders</span><span>{count(hp.orders)}</span></div>
            </>
          )}
        </div>
      )}
    </div>
  );
}
